import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ensureFavicon } from '../utils/favicon';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  structuredData?: object;
  noIndex?: boolean;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title = "CRMONCE - Microsoft Dynamics 365 & Power Platform Solutions | CRM Implementation & Consulting",
  description = "CRMONCE is a leading Microsoft Dynamics 365 and Power Platform consulting company. We specialize in CRM implementation, Power Apps development, Power Automate, and business transformation solutions.",
  keywords = "Microsoft Dynamics 365, Power Platform, CRM implementation, Power Apps, Power Automate, Power BI, Dynamics 365 consulting",
  canonical = "https://crmonce.com",
  ogImage = "/images/og-image.jpg",
  ogType = "website",
  structuredData,
  noIndex = false
}) => {
  const fullTitle = title.includes('CRMONCE') ? title : `${title} | CRMONCE`;
  const fullCanonical = canonical.startsWith('http') ? canonical : `https://crmonce.com${canonical}`;

  // Ensure favicon is always set correctly
  useEffect(() => {
    // Run immediately
    ensureFavicon();
    
    // Also run after a small delay to handle any conflicts
    const timer = setTimeout(() => {
      ensureFavicon();
    }, 500);
    
    // Run on page visibility change (when user returns to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        ensureFavicon();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fullTitle]); // Re-run when title changes

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow"} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullCanonical} />
      
      {/* Favicon and App Icons */}
      <link rel="icon" href={`/favicon.ico?v=${Date.now()}`} />
      <link rel="icon" type="image/png" sizes="32x32" href={`/favicon-32x32.png?v=${Date.now()}`} />
      <link rel="icon" type="image/png" sizes="16x16" href={`/favicon-16x16.png?v=${Date.now()}`} />
      <link rel="apple-touch-icon" href={`/apple-touch-icon.png?v=${Date.now()}`} />
      <link rel="shortcut icon" href={`/favicon.ico?v=${Date.now()}`} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={`https://crmonce.com${ogImage}`} />
      <meta property="og:site_name" content="CRMONCE" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={fullCanonical} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={`https://crmonce.com${ogImage}`} />
      <meta name="twitter:creator" content="@crmonce" />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead; 